import { useTranslation } from 'react-i18next';
import { fmtNum } from '../inventoryPeriod.js';
import { resolveAppLocale } from '../locale.js';

export interface StockCountRevertTarget {
  lineId: string;
  ingredientName: string;
  unit: string;
  countedAt: string;
  delta: number;
  currentStock: number;
}

export function StockCountRevertModal({
  target,
  note,
  saving,
  error,
  onNoteChange,
  onClose,
  onConfirm,
}: {
  target: StockCountRevertTarget;
  note: string;
  saving: boolean;
  error: string | null;
  onNoteChange: (v: string) => void;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = resolveAppLocale(i18n.language);
  const fmtDt = (iso: string) =>
    new Date(iso).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' });
  const deltaLabel = `${target.delta > 0 ? '+' : ''}${fmtNum(target.delta)}`;

  return (
    <div className="modal-overlay" onClick={() => !saving && onClose()}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('stockCount.revertModalTitle')}</h2>
          <button className="btn-icon" disabled={saving} onClick={onClose}>
            ×
          </button>
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 }}>
          {t('stockCount.revertModalBody', { delta: deltaLabel, unit: target.unit })}
        </p>
        <dl style={{ fontSize: 13, margin: '0 0 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
            <dt className="muted">{t('stockCount.ingredient')}</dt>
            <dd style={{ margin: 0, fontWeight: 600 }}>{target.ingredientName}</dd>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
            <dt className="muted">{t('stockCount.countDate')}</dt>
            <dd style={{ margin: 0 }}>{fmtDt(target.countedAt)}</dd>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
            <dt className="muted">{t('stockCount.countDelta')}</dt>
            <dd style={{ margin: 0 }}>
              {deltaLabel} {target.unit}
            </dd>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
            <dt className="muted">{t('stockCount.currentStock')}</dt>
            <dd style={{ margin: 0 }}>
              {fmtNum(target.currentStock)} {target.unit}
            </dd>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <dt className="muted">{t('stockCount.afterRevert')}</dt>
            <dd style={{ margin: 0, fontWeight: 600 }}>
              {fmtNum(target.currentStock - target.delta)} {target.unit}
            </dd>
          </div>
        </dl>
        <label style={{ display: 'block', fontSize: 13, marginBottom: 16 }}>
          <span style={{ fontWeight: 600 }}>{t('stockCount.reasonOptional')}</span>
          <input
            type="text"
            value={note}
            onChange={(e) => onNoteChange(e.target.value)}
            placeholder={t('stockCount.reasonPlaceholder')}
            style={{ display: 'block', width: '100%', marginTop: 6 }}
            disabled={saving}
          />
        </label>
        {error && (
          <div className="error-banner" style={{ marginBottom: 12 }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn-ghost" disabled={saving} onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            className="btn"
            style={{ background: 'var(--danger)', borderColor: 'var(--danger)', color: '#fff' }}
            disabled={saving}
            onClick={onConfirm}
          >
            {saving ? '…' : t('stockCount.confirmRevert')}
          </button>
        </div>
      </div>
    </div>
  );
}
